if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };

      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);

      if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener(
          'click',
          this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
        );
      });

      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
        this.removeListSemantic();
      }
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(
        `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
      );

      this.setActiveThumbnail(thumbnail);
    }

    rotateItemsFrom(container, activeElement, selector) {
      if (!container || !activeElement) return;

      const items = Array.from(container.querySelectorAll(selector));
      const activeIndex = items.indexOf(activeElement);

      if (activeIndex <= 0) return;

      const reorderedItems = items.slice(activeIndex).concat(items.slice(0, activeIndex));

      reorderedItems.forEach((item) => {
        container.appendChild(item);
      });
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) return;

      if (prepend) {
        this.rotateItemsFrom(
          activeMedia.parentElement,
          activeMedia,
          '[data-media-id]'
        );

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);

          if (activeThumbnail) {
            this.rotateItemsFrom(
              activeThumbnail.parentElement,
              activeThumbnail,
              '[data-target]'
            );
          }
        }

        if (this.elements.viewer.slider && typeof this.elements.viewer.resetPages === 'function') {
          this.elements.viewer.resetPages();
        }

        if (
          this.elements.thumbnails &&
          this.elements.thumbnails.slider &&
          typeof this.elements.thumbnails.resetPages === 'function'
        ) {
          this.elements.thumbnails.resetPages();
        }
      }

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });

      activeMedia.classList.add('is-active');

      this.preventStickyHeader();

      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }

        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({ behavior: 'smooth' });
        }
      });

      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;

      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      this.setActiveThumbnail(activeThumbnail);

      if (activeThumbnail) {
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => {
        element.removeAttribute('aria-current');
      });

      thumbnail.querySelector('button').setAttribute('aria-current', true);

      if (
        typeof this.elements.thumbnails.isSlideVisible === 'function' &&
        this.elements.thumbnails.isSlideVisible(thumbnail, 10)
      ) {
        return;
      }

      if (this.elements.thumbnails.slider) {
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image || !this.elements.liveRegion) return;

      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );

        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };

      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();

      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;

      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;

      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach((slide) => {
        slide.setAttribute('role', 'presentation');
      });
    }
  });
}

(function () {
  const SYNC_DELAYS = [80, 400, 900, 1500];

  function normalizeImageUrl(url) {
    if (!url) return '';

    try {
      const parsedUrl = new URL(url, window.location.origin);

      return parsedUrl.pathname
        .replace(/_(\d+x\d*|x\d+)\.(jpg|jpeg|png|webp|gif)$/i, '.$2')
        .toLowerCase();
    } catch (error) {
      return String(url)
        .split('?')[0]
        .replace(/_(\d+x\d*|x\d+)\.(jpg|jpeg|png|webp|gif)$/i, '.$2')
        .toLowerCase();
    }
  }

  function getImageKeys(img) {
    if (!img) return [];

    const keys = [
      img.currentSrc,
      img.src,
      img.getAttribute('src')
    ]
      .map(normalizeImageUrl)
      .filter(Boolean);

    if (img.getAttribute('srcset')) {
      img.getAttribute('srcset').split(',').forEach((part) => {
        const src = part.trim().split(' ')[0];
        const key = normalizeImageUrl(src);

        if (key) keys.push(key);
      });
    }

    return Array.from(new Set(keys));
  }

  function getGallery(sectionId) {
    return (
      document.querySelector(`media-gallery[data-section="${sectionId}"]:not([id$="-duplicate"])`) ||
      document.querySelector(`media-gallery[data-section="${sectionId}"]`)
    );
  }

  function getVariantPicker(sectionId) {
    return document.querySelector(`variant-selects[data-section="${sectionId}"]`);
  }

  function getSelectedVersionImage(picker) {
    if (!picker) return null;

    const checkedInputs = Array.from(
      picker.querySelectorAll('input[type="radio"]:checked')
    );

    for (const input of checkedInputs) {
      const swatch = input.closest('.color-swatch');

      if (!swatch) continue;

      const img = swatch.querySelector('.color-swatch__image img');

      if (img) return img;
    }

    return null;
  }

  function findMediaIdByImage(gallery, targetImage) {
    if (!gallery || !targetImage) return null;

    const targetKeys = getImageKeys(targetImage);

    if (!targetKeys.length) return null;

    const mediaElements = Array.from(
      gallery.querySelectorAll('[data-target], [data-media-id]')
    );

    for (const element of mediaElements) {
      const img = element.querySelector('img');

      if (!img) continue;

      const elementKeys = getImageKeys(img);

      const isMatch = elementKeys.some((elementKey) => {
        return targetKeys.some((targetKey) => {
          return (
            elementKey === targetKey ||
            elementKey.split('/').pop() === targetKey.split('/').pop()
          );
        });
      });

      if (isMatch) {
        return element.dataset.target || element.dataset.mediaId;
      }
    }

    return null;
  }

  function syncGalleryFromSelectedVersion(sectionId) {
    if (!sectionId) return;

    const picker = getVariantPicker(sectionId);
    const gallery = getGallery(sectionId);

    if (!picker || !gallery || typeof gallery.setActiveMedia !== 'function') return;

    const selectedVersionImage = getSelectedVersionImage(picker);
    if (!selectedVersionImage) return;

    const mediaId = findMediaIdByImage(gallery, selectedVersionImage);
    if (!mediaId) return;

    gallery.setActiveMedia(mediaId, true);
  }

  function scheduleSync(sectionId) {
    if (!sectionId) return;

    SYNC_DELAYS.forEach((delay) => {
      setTimeout(() => {
        syncGalleryFromSelectedVersion(sectionId);
      }, delay);
    });
  }

  document.addEventListener('change', function (event) {
    const picker = event.target.closest('variant-selects[data-section]');
    if (!picker) return;

    scheduleSync(picker.dataset.section);
  });

  document.addEventListener('click', function (event) {
    const picker = event.target.closest('variant-selects[data-section]');
    if (!picker) return;

    scheduleSync(picker.dataset.section);
  }, true);

  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      if (!event || !event.data || !event.data.sectionId) return;

      scheduleSync(event.data.sectionId);
    });
  }

  window.addEventListener('load', function () {
    document.querySelectorAll('variant-selects[data-section]').forEach((picker) => {
      scheduleSync(picker.dataset.section);
    });
  });
})();